import * as React from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useSearch } from '@tanstack/react-router'
import {
  Plane,
  NotebookText,
  Wrench,
} from '@/components/icons'
import { cn, getDutyStatusColor, getInitials } from '@/lib/utils'
import { DutyStatus } from '../../../api/src/shared'
import { formatDateKey } from './ui/roster-calendar'
import { useRosterData } from '../lib/hooks/useRosterData'
import { useAircraftMaintenance } from '../lib/hooks'
import type { RosterFilters } from './RosterFilterDrawer'
import { EmptyState } from './EmptyState'
import { CalendarDays } from '@/components/icons'
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from './ui/avatar'

// Types for roster entry with crew info
interface CrewInfo {
  id: string
  name: string
  firstName?: string | null
  lastName?: string | null
  imageUrl?: string | null
  avatarColor?: string | null
  crewRole?: string
}

interface RosterEntryWithCrew {
  id: string
  date: Dayjs
  crewId: string
  dutyStatus: DutyStatus
  crew?: CrewInfo
  hasNote?: boolean
}

// Map of date to array of roster entries for that day
type DayRosterMap = Record<string, RosterEntryWithCrew[]>

export type RosterCalendarMode = 'me' | 'team'

export interface RosterSummary {
  onDuty: number
  offDuty: number
  training: number
  standby: number
  leave: number
  sick: number
  positioning: number
}

export interface RosterCalendarViewProps {
  onDayClick?: (date: Date, entries: RosterEntryWithCrew[]) => void
  className?: string
  filters?: RosterFilters
  /** 'me' shows colored status dots, 'team' shows crew avatars (default: 'team') */
  mode?: RosterCalendarMode
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

// Render the first on-duty crew avatar and a +N chip counting additional on-duty crew
function CrewAvatars({ entries }: { entries: RosterEntryWithCrew[] }) {
  const onDutyCrew: CrewInfo[] = []
  const seen = new Set<string>()
  for (const entry of entries) {
    if (entry.dutyStatus !== DutyStatus.OnDuty) continue
    if (!entry.crew || seen.has(entry.crewId)) continue
    seen.add(entry.crewId)
    onDutyCrew.push(entry.crew)
  }

  if (onDutyCrew.length === 0) return null

  const visible = onDutyCrew.slice(0, 3)
  const overflow = onDutyCrew.length - visible.length

  return (
    <AvatarGroup>
      {visible.map((crew) => (
        <Avatar key={crew.id} className="size-4">
          {crew.imageUrl ? <AvatarImage src={crew.imageUrl} /> : null}
          <AvatarFallback
            className="text-background text-[8px] font-semibold"
            style={
              crew.avatarColor
                ? { backgroundColor: crew.avatarColor }
                : undefined
            }
          >
            {getInitials(crew.firstName, crew.lastName)}
          </AvatarFallback>
        </Avatar>
      ))}
      {overflow > 0 && (
        <AvatarGroupCount className="size-4 text-[8px] font-semibold bg-surface-brand-L0 text-content-primary-bold">
          +{overflow}
        </AvatarGroupCount>
      )}
    </AvatarGroup>
  )
}

// Render colored status dots for "me" mode calendar cells
function StatusDots({ entries }: { entries: RosterEntryWithCrew[] }) {
  // Get unique statuses
  const uniqueStatuses = Array.from(new Set(entries.map((e) => e.dutyStatus)))
  if (uniqueStatuses.length === 0) return null

  return (
    <div className="flex gap-1 items-center justify-center flex-wrap">
      {uniqueStatuses.map((status) => (
        <div
          key={status}
          className={cn('size-3 rounded-full', getDutyStatusColor(status))}
        />
      ))}
    </div>
  )
}

export function RosterCalendarView({
  onDayClick,
  className,
  filters,
  mode = 'team',
}: RosterCalendarViewProps) {
  const search = useSearch({ strict: false }) as { selectedMonth?: string }

  // Initialize from URL param if available, otherwise use current month
  const [currentMonth, setCurrentMonth] = React.useState(() => {
    if (search.selectedMonth) {
      const parsed = dayjs(search.selectedMonth, 'YYYY-MM')
      if (parsed.isValid()) {
        return parsed
      }
    }
    return dayjs()
  })

  // Sync with URL param when it changes
  React.useEffect(() => {
    if (search.selectedMonth) {
      const parsed = dayjs(search.selectedMonth, 'YYYY-MM')
      if (parsed.isValid() && !parsed.isSame(currentMonth, 'month')) {
        setCurrentMonth(parsed)
      }
    }
  }, [search.selectedMonth])

  // Calculate date range for current month
  const monthStart = currentMonth.startOf('month').format('YYYY-MM-DD')
  const monthEnd = currentMonth.endOf('month').format('YYYY-MM-DD')

  // Use shared roster data hook
  const { rosterEntries, crewMembers, crewFlightMap, isLoading } =
    useRosterData({
      startDate: monthStart,
      endDate: monthEnd,
      aircraftIds: filters?.aircraftIds,
    })

  // Maintenance lookup: selected aircraft, or all aircraft when none selected
  const { getMaintenanceForDay } = useAircraftMaintenance(filters?.aircraftIds)

  // Build map of crew by ID for quick lookup
  const crewMap = React.useMemo(() => {
    const map = new Map<string, CrewInfo>()
    crewMembers.forEach((crew) => {
      map.set(crew.id, {
        id: crew.id,
        name: crew.name,
        firstName: crew.firstName,
        lastName: crew.lastName,
        imageUrl: crew.imageUrl,
        avatarColor: crew.avatarColor,
        crewRole: crew.crewRole,
      })
    })
    return map
  }, [crewMembers])

  // Build day roster map from roster entries, applying filters
  const dayRosterMap: DayRosterMap = React.useMemo(() => {
    const map: DayRosterMap = {}

    rosterEntries.forEach((entry) => {
      // Apply crew filter
      if (filters?.crewMemberIds?.length && !filters.crewMemberIds.includes(entry.crewId)) {
        return
      }
      // Apply duty status filter
      if (filters?.dutyStatuses?.length && !filters.dutyStatuses.includes(entry.dutyStatus)) {
        return
      }

      const dateKey = formatDateKey(entry.date)
      const crew = crewMap.get(entry.crewId)

      const rosterEntry: RosterEntryWithCrew = {
        id: entry.id,
        date: entry.date,
        crewId: entry.crewId,
        dutyStatus: entry.dutyStatus,
        hasNote: !!entry.note,
        crew: crew || {
          id: entry.crewId,
          name: 'Unknown',
          firstName: null,
          lastName: null,
        },
      }

      if (!map[dateKey]) {
        map[dateKey] = []
      }
      map[dateKey].push(rosterEntry)
    })

    return map
  }, [rosterEntries, crewMap, filters?.crewMemberIds, filters?.dutyStatuses])

  // Build a set of dates that have trip legs from the crewFlightMap
  // When filtering by a specific crew member, only show flights for that crew
  const datesWithTrips = React.useMemo(() => {
    const dateSet = new Set<string>()
    Object.entries(crewFlightMap).forEach(([crewId, crewDates]) => {
      if (filters?.crewMemberIds?.length && !filters.crewMemberIds.includes(crewId)) return
      Object.keys(crewDates).forEach((dateKey) => {
        dateSet.add(dateKey)
      })
    })
    return dateSet
  }, [crewFlightMap, filters?.crewMemberIds])

  // Check if filters are active and no entries match
  const hasActiveFilters = !!(filters?.crewMemberIds?.length || filters?.dutyStatuses?.length)
  const hasNoMatchingEntries = Object.keys(dayRosterMap).length === 0

  // Calendar calculations
  const daysInMonth = currentMonth.daysInMonth()
  const firstDayOfMonth = currentMonth.startOf('month').day()
  const prevMonth = currentMonth.subtract(1, 'month')
  const prevMonthDays = prevMonth.daysInMonth()
  const prevMonthStartDay = prevMonthDays - firstDayOfMonth + 1
  const totalDays = firstDayOfMonth + daysInMonth
  const totalRows = Math.ceil(totalDays / 7)
  const totalCells = totalRows * 7

  const handleDayClick = (date: Dayjs, entries: RosterEntryWithCrew[]) => {
    onDayClick?.(date.toDate(), entries)
  }

  const renderDays = () => {
    const days: React.ReactNode[] = []

    for (let i = 0; i < totalCells; i++) {
      let dayNumber: number
      let isCurrentMonth: boolean
      let date: Dayjs

      if (i < firstDayOfMonth) {
        // Previous month days
        dayNumber = prevMonthStartDay + i
        isCurrentMonth = false
        date = prevMonth.date(dayNumber)
      } else if (i < firstDayOfMonth + daysInMonth) {
        // Current month days
        dayNumber = i - firstDayOfMonth + 1
        isCurrentMonth = true
        date = currentMonth.date(dayNumber)
      } else {
        // Next month days
        dayNumber = i - firstDayOfMonth - daysInMonth + 1
        isCurrentMonth = false
        date = currentMonth.add(1, 'month').date(dayNumber)
      }

      const dateKey = formatDateKey(date)
      const entries = dayRosterMap[dateKey] || []
      const hasEntries = entries.length > 0
      const hasTrip = datesWithTrips.has(dateKey)
      const hasNote = entries.some((e) => e.hasNote)
      const maintenanceAircraft = isCurrentMonth
        ? getMaintenanceForDay(date)
        : []
      const isMaintenance = maintenanceAircraft.length > 0
      const isToday = isCurrentMonth && date.isSame(dayjs(), 'day')

      days.push(
        <button
          key={i}
          type="button"
          onClick={() => handleDayClick(date, entries)}
          disabled={!isCurrentMonth}
          className={cn(
            'flex flex-col items-center gap-1 px-1 py-2 rounded h-19',
            'transition-colors',
            !isCurrentMonth && 'opacity-50 bg-surface-L0 p-2',
            isCurrentMonth && 'bg-surface-L0',
            isCurrentMonth && 'hover:ring-[1px] hover:ring-primary/20'
          )}
        >
          {/* Day number */}
          {isToday ? (
            <div className="flex items-center justify-center size-6 rounded-full bg-background-brand-bold">
              <span className="text-sm font-medium text-content-primary-inverse tracking-[0.07px]">
                {dayNumber}
              </span>
            </div>
          ) : (
            <span className="text-sm leading-normal text-content-primary-bold">
              {dayNumber}
            </span>
          )}

          {/* Day content - avatars with trip/note indicators centered below */}
          {isCurrentMonth &&
            (hasEntries || hasTrip || hasNote || isMaintenance) && (
              <div className="flex flex-col items-center gap-1">
                {hasEntries &&
                  (mode === 'me' ? (
                    <StatusDots entries={entries} />
                  ) : (
                    <CrewAvatars entries={entries} />
                  ))}
                {(hasTrip || hasNote || isMaintenance) && (
                  <div className="flex items-center gap-0.5">
                    {hasTrip && (
                      <Plane className="size-4 text-content-primary-subtle" />
                    )}
                    {hasNote && (
                      <NotebookText className="size-4 text-content-primary-subtle" />
                    )}
                    {isMaintenance && (
                      <Wrench className="size-4 text-background-notice-bold shrink-0" />
                    )}
                  </div>
                )}
              </div>
            )}
        </button>
      )
    }

    return days
  }

  return (
    <div className={cn('w-full flex flex-col gap-4', className)}>
      {/* Calendar Grid or Empty State */}
      {!isLoading && hasActiveFilters && hasNoMatchingEntries ? (
        <EmptyState
          icon={CalendarDays}
          title="No roster entries"
          description="No roster entries match the current filters"
        />
      ) : (
        <div className="flex flex-col gap-px">
          {/* Weekday headers */}
          <div className="grid grid-cols-7">
            {WEEKDAYS.map((day, index) => {
              const isCurrentWeekday = dayjs().day() === index
              return (
                <div
                  key={day}
                  className={cn(
                    'flex items-center justify-center h-8 text-xs tracking-[0.18px]',
                    isCurrentWeekday
                      ? 'text-content-primary-bold font-medium'
                      : 'text-content-primary-subtle'
                  )}
                >
                  {day}
                </div>
              )
            })}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-px">{renderDays()}</div>
        </div>
      )}
    </div>
  )
}

export type { RosterEntryWithCrew }
